/**
 * The studio map's Path look (spec 025): the story as a game's level path,
 * start at the top. Each layer of the route is a row of chunky scene cards;
 * a thick path joins each card to the next and forks into one labelled
 * branch per choice. Loops are chips on their source card, end nodes finish
 * cards, and scenes off the path wait in a tray below.
 *
 * Read-only for structure: nothing here adds, removes, moves or connects.
 * Cards are buttons in stop order, so Tab walks the story.
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { RouteNode, StoryRoute } from '../../utils/storyRoute';
import SceneCard from './SceneCard';
import type { Thumb } from './sceneThumbs';
import styles from './StoryStudio.module.scss';

export interface CardBadges {
  missingMedia?: boolean;
  needsFix?: boolean;
}

export interface StoryMapProps {
  route: StoryRoute;
  startNodeId: string;
  selectedNodeId: string | null;
  onSelect: (nodeId: string) => void;
  thumbs: Map<number, Thumb>;
  srcFor: (mediaId: number) => string | undefined;
  badges: Map<string, CardBadges>;
  /** Overrides `prefers-reduced-motion`. */
  reducedMotion?: boolean;
}

interface Segment {
  id: string;
  d: string;
}

/** Single-card rows swing gently left and right of the centre (px). */
const swing = (layer: number) => (layer % 4 === 1 ? 56 : layer % 4 === 3 ? -56 : 0);

export const StoryMap: React.FC<StoryMapProps> = ({
  route, startNodeId, selectedNodeId, onSelect, thumbs, srcFor, badges, reducedMotion,
}) => {
  const innerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef(new Map<string, HTMLElement>());
  const [segments, setSegments] = useState<Segment[]>([]);

  const rows = useMemo(() => {
    const out: RouteNode[][] = [];
    for (const n of route.nodes) (out[n.layer] ||= []).push(n);
    return out.filter(Boolean);
  }, [route]);
  const nameById = useMemo(
    () => new Map([...route.nodes, ...route.offPath].map((n) => [n.id, n.name])),
    [route],
  );
  const outgoing = useMemo(() => {
    const m = new Map<string, typeof route.edges>();
    for (const e of route.edges) (m.get(e.from) ?? m.set(e.from, []).get(e.from)!).push(e);
    return m;
  }, [route]);

  // The path: one rounded curve per forward edge, from a card's bottom to the next card's top.
  const measure = useCallback(() => {
    const inner = innerRef.current;
    if (!inner) return;
    const box = inner.getBoundingClientRect();
    const placed = new Set(route.nodes.map((n) => n.id));
    const next: Segment[] = [];
    for (const e of route.edges) {
      if (e.loopBack || !placed.has(e.from) || !placed.has(e.to)) continue;
      const a = cardRefs.current.get(e.from)?.getBoundingClientRect();
      const b = cardRefs.current.get(e.to)?.getBoundingClientRect();
      if (!a || !b || (!a.width && !b.width)) continue;
      const x1 = a.left + a.width / 2 - box.left;
      const y1 = a.bottom - box.top;
      const x2 = b.left + b.width / 2 - box.left;
      const y2 = b.top - box.top;
      const my = (y1 + y2) / 2;
      next.push({ id: e.id, d: `M ${x1} ${y1} C ${x1} ${my}, ${x2} ${my}, ${x2} ${y2}` });
    }
    setSegments((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
  }, [route]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);
  useEffect(() => {
    const inner = innerRef.current;
    if (!inner || typeof ResizeObserver !== 'function') return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(inner);
    return () => ro.disconnect();
  }, [measure]);

  // The selected scene's card scrolls into view, however it was chosen (a
  // click, a choice in the preview, the playing story, a turn): smooth unless
  // reduced motion is preferred.
  useEffect(() => {
    if (!selectedNodeId) return;
    const el = cardRefs.current.get(selectedNodeId);
    if (!el || typeof el.scrollIntoView !== 'function') return;
    const reduced = reducedMotion ?? (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
  }, [selectedNodeId, reducedMotion]);

  const setRef = (id: string) => (el: HTMLElement | null) => {
    if (el) cardRefs.current.set(id, el);
    else cardRefs.current.delete(id);
  };

  const card = (n: RouteNode, small = false) => {
    const b = badges.get(n.id) ?? {};
    const thumb = n.kind === 'scene' ? thumbs.get(n.storySceneId) : undefined;
    const loops = (outgoing.get(n.id) ?? []).filter((e) => e.loopBack).map((e) => nameById.get(e.to) ?? '');
    return (
      <SceneCard
        ref={setRef(n.id)}
        kind={n.kind}
        name={n.name}
        stop={n.stop}
        thumb={thumb}
        thumbSrc={thumb && thumb.kind !== 'tile' ? srcFor(thumb.mediaId) : undefined}
        isStart={n.id === startNodeId}
        selected={n.id === selectedNodeId}
        missingMedia={b.missingMedia}
        needsFix={b.needsFix}
        loops={loops}
        small={small}
        onSelect={n.kind === 'scene' ? () => onSelect(n.id) : undefined}
        testId={`map-card-${n.id}`}
      />
    );
  };

  if (route.nodes.length === 0 && route.offPath.length === 0) {
    return (
      <div className={styles.pathMap} data-testid="story-map-path">
        <div className={styles.pathEmpty}>
          <div className={`${styles.card} ${styles.placeholderCard}`} data-testid="map-start-placeholder">
            <span className={styles.startRibbon}>Start</span>
            <div className={styles.cardName}>Your first scene</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pathMap} data-testid="story-map-path">
      <div className={styles.pathInner} ref={innerRef}>
        <svg className={styles.pathLines} aria-hidden="true">
          {segments.map((s) => <path key={s.id} d={s.d} className={styles.pathLine} />)}
        </svg>
        {rows.map((row, i) => (
          <div
            key={i}
            className={styles.pathRow}
            data-testid={`map-row-${i}`}
            style={row.length === 1 ? { transform: `translateX(${swing(i)}px)` } : undefined}
          >
            {row.map((n) => {
              const forks = (outgoing.get(n.id) ?? []).filter((e) => !e.loopBack && e.label !== undefined);
              return (
                <div key={n.id} className={styles.pathSlot}>
                  {card(n)}
                  {forks.length > 0 && (
                    <div className={styles.fork} data-testid={`map-fork-${n.id}`}>
                      {forks.map((e) => (
                        <span key={e.id} className={styles.branch} data-testid="map-branch">
                          {e.label || 'Untitled choice'}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
        {route.offPath.length > 0 && (
          <section className={styles.offPath} data-testid="map-off-path">
            <h4 className={styles.offPathTitle}>Not on the path yet</h4>
            <div className={styles.offPathCards}>
              {route.offPath.map((n) => <React.Fragment key={n.id}>{card(n, true)}</React.Fragment>)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default StoryMap;
